import React, { useEffect, useRef, useState } from "react";
import { useAuth, useFeatureFlag } from "@Hooks";
import { useQueuedVoiceCallFromUserId } from "@Components/conversation/conversation.messages/conversation.message/useQueuedVoiceCall";
import { CallStatus } from "@Api/abstract/queued_voice_call.api.abstract";
import { t } from "i18next";

export const QueuedVoiceCallNotification = React.memo(() => {
  const auth = useAuth();
  const isFeatureEnabled = useFeatureFlag("journey.call.back", false);

  const userId = auth?.user?.id;
  const hasVoiceLicence = auth?.user?.voice_license;
  const isMain = auth?.user?.main;
  const { queuedVoiceCall, fetchQueuedVoiceCalls, loading, error } =
    useQueuedVoiceCallFromUserId(userId);

  const [status, setStatus] = useState<CallStatus | null>(null);
  const [notificationText, setNotificationText] = useState<any>();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const queuedVoiceCallRef = useRef(queuedVoiceCall);

  const clearPolling = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  useEffect(() => {
    queuedVoiceCallRef.current = queuedVoiceCall;
  }, [queuedVoiceCall]);

  useEffect(() => {
    if (!hasVoiceLicence || !isMain || !isFeatureEnabled) return;

    const poll = async () => {
      fetchQueuedVoiceCalls();
      const currentStatus = queuedVoiceCallRef.current?.status ?? null;

      if (currentStatus === CallStatus.CALLING_CUSTOMER) {
        setNotificationText({
          title: t("QUEUED_VOICE_CALL_NOTIFICATION_TITLE_CALLING_CUSTOMER"),
          description: t("QUEUED_VOICE_CALL_NOTIFICATION_TEXT_CALLING_CUSTOMER"),
        });
      } else if (currentStatus === CallStatus.CUSTOMER_ANSWERED) {
        setNotificationText({
          title: t("QUEUED_VOICE_CALL_NOTIFICATION_TITLE_CUSTOMER_ANSWERED"),
          description: t("QUEUED_VOICE_CALL_NOTIFICATION_TEXT_CUSTOMER_ANSWERED"),
        });
      } else {
        setNotificationText(null);
      }

      if (currentStatus !== status) {
        setStatus(currentStatus);
      }

      const nextDelay = 3000;

      timeoutRef.current = setTimeout(poll, nextDelay);
    };

    poll();

    return () => clearPolling();
  }, [hasVoiceLicence, isMain, userId]);

  if (!status || !notificationText || status === CallStatus.ONGOING) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 10,
        right: 10,
        backgroundColor: "var(--COLOR-000533)",
        color: "#fff",
        padding: "14px 20px",
        borderRadius: 8,
        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
        zIndex: 1000,
        maxWidth: 300,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{notificationText.title}</div>
      <div style={{ fontSize: 12, lineHeight: 1.4 }}>{notificationText.description}</div>
    </div>
  );
});
